import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useBackdropClose from './useBackdropClose';
import { useAuth } from './AuthProvider';
import { API_BASE } from './api';
import { LEGAL_LAST_UPDATED, OPERATOR, SUPERVISORY_AUTHORITY } from './legalInfo';

/* -------------------------------------------------------------------------- */
/* What Frolik keeps on your device                                            */
/* -------------------------------------------------------------------------- */

// Remembers that the storage notice was seen. Like everything else Frolik keeps
// on the device, this is strictly necessary (§ 25(2) no. 2 TDDDG), so no consent
// banner is needed: Frolik sets no cookies and runs no analytics or trackers.
export const PRIVACY_STORAGE_KEY = 'frolik_privacy_notice_seen';

export const DEVICE_STORAGE = [
  ['Sign-in', 'Keeps you signed in (a login token and your username).'],
  ['Chat name', 'The name you use in event chats, so you don’t retype it.'],
  ['Saved likes', 'Events you heart. Stored only on this device, never on our server.'],
  ['RSVPs', 'Which events you RSVP’d to here, so the button remembers.'],
  ['Notifications', 'Events you follow with “Notify me”, and the browser helper that shows them.'],
  ['This notice', 'That you’ve seen this storage notice.'],
];

export function hasSeenStorageNotice() {
  try {
    return Boolean(window.localStorage.getItem(PRIVACY_STORAGE_KEY));
  } catch (error) {
    return true; // Storage blocked: don't keep showing the notice.
  }
}

export function markStorageNoticeSeen() {
  try {
    window.localStorage.setItem(PRIVACY_STORAGE_KEY, '1');
  } catch (error) {
    // Ignore: storage is blocked.
  }
}

// Removes everything Frolik keeps in this browser (and stops notifications).
export async function clearFrolikDeviceData() {
  try {
    const keys = Object.keys(window.localStorage);
    for (const key of keys) {
      if (
        key.startsWith('frolik') ||
        key.startsWith('rsvps:') ||
        ['auth_user', 'auth_token', 'auth_users', 'chatName', 'events'].includes(key)
      ) {
        window.localStorage.removeItem(key);
      }
    }
  } catch (error) {
    // Storage blocked: nothing to clear.
  }

  try {
    if (window.indexedDB) window.indexedDB.deleteDatabase('frolik-device-saved-events');
  } catch (error) {
    // Ignore.
  }

  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration('/');
      const subscription = registration && (await registration.pushManager?.getSubscription());
      if (subscription) await subscription.unsubscribe();
      if (registration) await registration.unregister();
    }
  } catch (error) {
    // Ignore.
  }
}

/* -------------------------------------------------------------------------- */
/* One-time storage notice                                                     */
/* -------------------------------------------------------------------------- */

export function PrivacyConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!hasSeenStorageNotice());
  }, []);

  if (!open) {
    return null;
  }

  return (
    <div style={styles.banner} role="region" aria-label="Storage notice">
      <div style={styles.content}>
        <div style={styles.header}>No cookies, no trackers</div>
        <p style={styles.text}>
          Frolik doesn’t use cookies, analytics or ads. We only keep what’s needed on your device
          to keep you signed in and remember your choices, like likes, RSVPs and notifications.
          See <Link to="/privacy">our privacy policy</Link> or “Your data” in the footer.
        </p>
        <div style={styles.actions}>
          <button
            type="button"
            onClick={() => {
              markStorageNoticeSeen();
              setOpen(false);
            }}
            style={{ ...styles.button, ...styles.primaryButton }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* "Your data" dialog                                                          */
/* -------------------------------------------------------------------------- */

export function PrivacySettingsModal({ open, onClose }) {
  const auth = useAuth() || {};
  const { user, token, signOut } = auth;
  const backdropHandlers = useBackdropClose(onClose);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (open) {
      setMessage('');
      setDeleting(false);
      setPassword('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const clearDevice = async () => {
    if (!window.confirm('Remove everything Frolik keeps on this device? You’ll be signed out and lose saved likes here.')) return;
    setBusy(true);
    await clearFrolikDeviceData();
    window.location.assign('/');
  };

  const downloadData = async () => {
    setBusy(true);
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/account/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Download failed');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'frolik-my-data.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage('Your data was downloaded.');
    } catch (error) {
      setMessage(error.message || 'Download failed.');
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Deletion failed');
      await clearFrolikDeviceData();
      await signOut?.();
      window.location.assign('/');
    } catch (error) {
      setMessage(error.message || 'Deletion failed.');
      setBusy(false);
    }
  };

  return (
    <div style={styles.overlay} {...backdropHandlers} role="presentation">
      <div
        className="privacy-settings-modal"
        style={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-settings-title"
      >
        <div style={styles.modalHeader}>
          <div id="privacy-settings-title" className="privacy-settings-title" style={styles.modalTitle}>
            Your data
          </div>
          <button
            type="button"
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <p style={styles.modalCopy}>
          Frolik uses no cookies, analytics or ads. This is everything kept on this device, all of it
          needed for features you use:
        </p>

        <ul style={styles.storageList}>
          {DEVICE_STORAGE.map(([name, why]) => (
            <li key={name}><strong>{name}:</strong> {why}</li>
          ))}
        </ul>

        <div style={styles.modalActions}>
          <button type="button" onClick={clearDevice} disabled={busy} style={{ ...styles.modalButton, ...styles.secondaryButton }}>
            Clear Frolik data on this device
          </button>
        </div>

        {user && (
          <>
            <h3 style={styles.sectionTitle}>Your account</h3>
            <p style={styles.modalCopy}>
              Signed in as <strong>{user.username || user.name}</strong>. You can download everything
              we store about your account, or delete it.
            </p>
            <div style={styles.modalActions}>
              <button type="button" onClick={downloadData} disabled={busy} style={{ ...styles.modalButton, ...styles.secondaryButton }}>
                Download my data
              </button>
              {!deleting && (
                <button type="button" onClick={() => setDeleting(true)} disabled={busy} style={{ ...styles.modalButton, ...styles.dangerButton }}>
                  Delete my account
                </button>
              )}
            </div>

            {deleting && (
              <div style={styles.deleteBox}>
                <p style={{ ...styles.modalCopy, margin: '0 0 10px' }}>
                  This permanently deletes your account and every event you created, including their
                  chats. It can’t be undone. Enter your password to confirm.
                </p>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Your password"
                  aria-label="Your password"
                  autoComplete="current-password"
                  style={styles.passwordInput}
                />
                <div style={styles.modalActions}>
                  <button type="button" onClick={() => setDeleting(false)} disabled={busy} style={{ ...styles.modalButton, ...styles.secondaryButton }}>
                    Cancel
                  </button>
                  <button type="button" onClick={deleteAccount} disabled={busy || !password} style={{ ...styles.modalButton, ...styles.dangerButton }}>
                    Permanently delete
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {message && <p role="status" style={styles.status}>{message}</p>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Legal pages                                                                 */
/* -------------------------------------------------------------------------- */

const PROCESSORS = [
  ['Netlify', 'hosts the website you load', 'USA'],
  ['Render', 'runs the Frolik server and stores its access logs (IP addresses, for security)', 'USA or EU, depending on the server region'],
  ['MongoDB database host', 'stores accounts, events, comments, reports and notification addresses', '[region of your database host]'],
  ['Komoot (Photon, OpenStreetMap data)', 'suggests venues, businesses and addresses when you type a location', 'Germany'],
  ['Mapbox', 'also suggests places when you type a location, and turns “Use my location” (rounded to about 100 m) into a city', 'USA'],
  ['Cloudinary', 'stores pictures you upload for events', 'USA'],
  ['Google (Firebase Cloud Messaging) and Apple Push Notification service', 'deliver notifications to your device if you tap “Notify me”', 'USA'],
  ['Resend', 'sends invitation emails to addresses you enter', 'USA'],
];

export function PrivacyPage() {
  return (
    <div style={pageStyles.page}>
      <div style={pageStyles.card}>
        <h1 style={pageStyles.title}>Privacy policy</h1>
        <p style={pageStyles.meta}>Last updated {LEGAL_LAST_UPDATED}</p>

        <p style={pageStyles.text}>
          Frolik is built to know as little about you as possible. Accounts have no real name, email
          address or phone number. There are no cookies, analytics, ads or trackers.
        </p>

        <h2 style={pageStyles.subtitle}>1. What we process, why, and on what legal basis</h2>
        <ul style={pageStyles.list}>
          <li><strong>Your account:</strong> username, password (stored only in scrambled form), role, and when you accepted the sign-up agreements. To provide your account (Art. 6(1)(b) GDPR).</li>
          <li><strong>Profile picture (optional):</strong> if you add one, it’s stored by Cloudinary and shown next to your name in the footer. You can remove it any time on your profile page. To provide your account (Art. 6(1)(b) GDPR).</li>
          <li><strong>Events and comments you post:</strong> what you write, the picture you upload, the location you enter, and the name you comment under. If you comment while signed in, the comment is also linked to your account (never shown publicly) so we can tell you about later comments. To run the service (Art. 6(1)(b) GDPR). Public events are visible to everyone; private events to people with the link.</li>
          <li><strong>Email addresses you invite:</strong> stored with the event and used once to send the invitation. In the legitimate interest of you and the people you invite (Art. 6(1)(f) GDPR). Never used for anything else.</li>
          <li><strong>RSVPs:</strong> only a count per event; we don’t record who RSVP’d.</li>
          <li><strong>Notifications:</strong> if you tap “Notify me”, a notification address your browser gives us for that event. Based on your consent (Art. 6(1)(a) GDPR), which you can withdraw by tapping the button again.</li>
          <li><strong>Location:</strong> what you type in a location field is sent to Komoot (Photon) and Mapbox for suggestions, together with a rough area (the last place you picked on this device, rounded to about 1 km, or Berlin) so nearby places come first. That rough area is kept only on your device. “Use my location”, or creating an event with an empty location, sends a position rounded to about 100 m to Mapbox to find your city, after your browser asks your permission. We never store your position. Your city is only saved as an event’s location if you confirm it, and the event’s map point is then the city centre, not where you are.</li>
          <li><strong>In-app notifications:</strong> messages in the footer bell about your events being approved or declined, and new comments on events you created or commented on. To run the service (Art. 6(1)(b) GDPR).</li>
          <li><strong>Moderation and reports:</strong> our review decisions and reasons, and reports people send (the reason and details only, not who reported). To keep Frolik safe and meet our duties under the EU Digital Services Act (Art. 6(1)(c) and (f) GDPR).</li>
          <li><strong>Server logs:</strong> our hosting providers record technical data such as IP addresses to keep the service secure (Art. 6(1)(f) GDPR).</li>
        </ul>

        <h2 style={pageStyles.subtitle}>2. On your device: no cookies</h2>
        <p style={pageStyles.text}>
          We set no cookies. Frolik keeps a few things in your browser’s storage, all strictly necessary
          for features you use (§ 25(2) no. 2 TDDDG), so we don’t ask for consent:
        </p>
        <ul style={pageStyles.list}>
          {DEVICE_STORAGE.map(([name, why]) => (
            <li key={name}><strong>{name}:</strong> {why}</li>
          ))}
        </ul>
        <p style={{ ...pageStyles.text, marginTop: 8 }}>
          You can remove all of it with “Your data” → “Clear Frolik data on this device” in the footer.
        </p>

        <h2 style={pageStyles.subtitle}>3. Service providers</h2>
        <p style={pageStyles.text}>We use these providers, each bound to process data only for us:</p>
        <ul style={pageStyles.list}>
          {PROCESSORS.map(([name, role, where]) => (
            <li key={name}><strong>{name}</strong> ({where}): {role}.</li>
          ))}
        </ul>
        <p style={{ ...pageStyles.text, marginTop: 8 }}>
          Transfers to the USA are protected by the EU-US Data Privacy Framework or the EU Standard
          Contractual Clauses. Notification content is encrypted so delivery services can’t read it.
        </p>

        <h2 style={pageStyles.subtitle}>4. How long we keep data</h2>
        <ul style={pageStyles.list}>
          <li>Your account and events: until you delete them, or we remove content that breaks our terms.</li>
          <li>Notification addresses: until you turn notifications off or your browser revokes them.</li>
          <li>Resolved reports: 6 months, then deleted automatically.</li>
          <li>In-app notifications: 60 days, then deleted automatically.</li>
          <li>Server logs: as long as our hosting providers keep them, usually a few days to weeks.</li>
        </ul>

        <h2 style={pageStyles.subtitle}>5. Your rights</h2>
        <p style={pageStyles.text}>
          You can access your data and take a copy (“Your data” → “Download my data”), have it corrected,
          delete your account (“Your data” → “Delete my account”), restrict or object to processing, and
          withdraw consent at any time. For anything else, email <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>. You can also complain to
          a data protection authority, for example the{' '}
          <a href={SUPERVISORY_AUTHORITY.url} target="_blank" rel="noopener noreferrer">{SUPERVISORY_AUTHORITY.name}</a>,
          or the one where you live.
        </p>

        <h2 style={pageStyles.subtitle}>6. Age, profiling and changes</h2>
        <p style={pageStyles.text}>
          Frolik is for people aged 16 and over. We don’t make automated decisions about you or build
          profiles. If this policy changes, we’ll update the date above.
        </p>
      </div>
    </div>
  );
}

export function TermsPage() {
  return (
    <div style={pageStyles.page}>
      <div style={pageStyles.card}>
        <h1 style={pageStyles.title}>About Frolik</h1>
        <p style={pageStyles.text}>
          Frolik is a place to find and share local events. Anyone can browse; with a free account you
          can create events, RSVP, chat and share collectible event cards.
        </p>

        <h1 style={{ ...pageStyles.title, marginTop: 28 }}>Terms of Service</h1>
        <p style={pageStyles.meta}>Last updated {LEGAL_LAST_UPDATED}</p>

        <h2 style={pageStyles.subtitle}>1. Who can use Frolik</h2>
        <p style={pageStyles.text}>
          You must be at least 16. Choose a username that isn’t your real name, and keep your password
          to yourself. You’re responsible for what happens under your account.
        </p>

        <h2 style={pageStyles.subtitle}>2. What’s not allowed</h2>
        <ul style={pageStyles.list}>
          <li>Anything illegal, including scams, fraud, and selling illegal goods or services</li>
          <li>Harassment, hate, threats, or planning violence or harm</li>
          <li>Misleading or fake events, spam, or invitations to people who don’t know you</li>
          <li>Pretending to be someone else, or sharing other people’s private information</li>
          <li>Content that infringes copyright or other rights</li>
        </ul>

        <h2 style={pageStyles.subtitle}>3. Moderation and reports</h2>
        <p style={pageStyles.text}>
          Public events are reviewed before they appear on the home page. Anyone can report an event
          with the Report button on its page. If we remove or decline an event, its creator sees the
          reason on the event. We may suspend accounts that seriously or repeatedly break these terms,
          and we cooperate with valid requests from authorities. If you disagree with a decision, email
          us at <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>.
        </p>

        <h2 style={pageStyles.subtitle}>4. Events are organised by users</h2>
        <p style={pageStyles.text}>
          Frolik isn’t the organiser of events listed here. Details can change, so check with the
          organiser. We’re fully liable for intent, gross negligence, and injury to life, body or health;
          otherwise only for breaching essential obligations, limited to the typical, foreseeable damage.
        </p>

        <h2 style={pageStyles.subtitle}>5. Ending, changes and law</h2>
        <p style={pageStyles.text}>
          You can delete your account at any time under “Your data”. We may change these terms and will
          update the date above. German law applies; consumer protections of the country you live in
          aren’t affected.
        </p>

        <h2 style={pageStyles.subtitle}>6. Contact</h2>
        <p style={pageStyles.text}>
          Our point of contact for users and authorities (EU Digital Services Act) is <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>,
          in English or German. See the <Link to="/contact">contact page</Link> and the{' '}
          <Link to="/privacy">privacy policy</Link>.
        </p>
      </div>
    </div>
  );
}

const pageStyles = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 16px 140px',
  },
  card: {
    width: 'min(820px, 100%)',
    background: 'rgba(255,255,255,0.92)',
    borderRadius: 4.5,
    padding: '24px 20px',
    boxShadow: '0 12px 28px rgba(134,69,16,0.18)',
    color: '#111827',
  },
  title: {
    margin: '0 0 6px',
    fontSize: '1.6rem',
  },
  meta: {
    margin: '0 0 16px',
    color: '#8c7059',
  },
  subtitle: {
    margin: '20px 0 8px',
    fontSize: '1rem',
  },
  text: {
    margin: 0,
    color: '#374151',
  },
  list: {
    margin: '0 0 0 18px',
    padding: 0,
    color: '#374151',
  },
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding: 20,
    background: 'rgba(17, 24, 39, 0.58)',
    backdropFilter: 'blur(7px)',
    WebkitBackdropFilter: 'blur(7px)',
  },
  modal: {
    width: 'min(560px, 100%)',
    maxHeight: 'min(720px, calc(100vh - 40px))',
    overflowY: 'auto',
    boxSizing: 'border-box',
    padding: '24px',
    border: '1px solid rgba(255,255,255,0.9)',
    borderRadius: 4.5,
    background: 'linear-gradient(180deg, #fff 0%, #fcf7f3 100%)',
    color: '#111827',
    boxShadow: '0 24px 80px rgba(0,0,0,0.32), inset 0 1px 0 #fff',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  modalTitle: {
    fontWeight: 700,
    fontSize: 21,
    color: '#111827',
  },
  sectionTitle: {
    margin: '20px 0 6px',
  },
  modalCopy: {
    margin: '0 0 12px',
    color: '#4b5563',
  },
  storageList: {
    margin: '0 0 4px 18px',
    padding: 0,
    color: '#374151',
  },
  closeButton: {
    width: 34,
    height: 34,
    flex: '0 0 34px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    border: '1px solid rgba(17,24,39,0.14)',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.9)',
    color: '#111827',
    fontSize: 23,
    cursor: 'pointer',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 14,
  },
  modalButton: {
    border: '1px solid rgba(17,24,39,0.16)',
    borderRadius: '5px',
    padding: '9px 13px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  deleteBox: {
    marginTop: 14,
    padding: 14,
    border: '1px solid #f1b8b2',
    borderRadius: 4,
    background: '#fff5f4',
  },
  passwordInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 10px',
    border: '1px solid #d0c1a5',
    borderRadius: 3,
  },
  status: {
    margin: '14px 0 0',
    color: '#98551f',
  },
  banner: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    display: 'flex',
    justifyContent: 'center',
    padding: '12px',
    background: 'rgba(17, 24, 39, 0.5)',
  },
  content: {
    width: 'min(640px, 100%)',
    background: '#fff',
    color: '#111827',
    borderRadius: '4.5px',
    border: '1px solid rgba(17, 24, 39, 0.1)',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.2)',
    padding: '16px 18px 14px',
  },
  header: {
    fontWeight: 700,
    marginBottom: '8px',
  },
  text: {
    margin: '0 0 8px',
    color: '#374151',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '8px',
  },
  button: {
    border: 'none',
    borderRadius: '5px',
    padding: '9px 22px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  secondaryButton: {
    background: '#faf1e9',
    color: '#512f14',
  },
  primaryButton: {
    background: '#e66700',
    color: '#fff',
  },
  dangerButton: {
    background: '#fff',
    borderColor: '#b42318',
    color: '#b42318',
  },
};

export default PrivacyConsentBanner;
